import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaRegTrashAlt } from 'react-icons/fa';
import toast from 'react-hot-toast';
import {
  Button,
  Checkbox,
  EmptyPlaceholder,
  isDefinedString,
  ItemsListAssignment,
  Modal,
  ModalContent,
  ModalFooter,
  Pagination,
  SearchField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableHeadIconCell,
  TableHeadRow,
  TableIconCell,
  TableRow,
  Text,
  useSearching,
  isEqualByIds,
} from '@client/toolkit';

export type SelectionModalProps<T> = {
  onSubmit: (items: T[]) => void;
  onClose: () => void;
  open?: boolean;
  selected?: T[];
  title: string;
  searchQuery: (params: { search: string; 'pagination.Limit': number; 'pagination.Offset': number }) => any;
  getItemName: (item: T) => string;
  getItemSubtitle?: (item: T) => string;
  getItemDetails?: (item: T) => { label: string; value: string }[];
  entityName: string;
  maxSelectedItems?: number;
  maxSelectedItemsMessage?: string;
  disabledIds?: string[];
};

export function SelectionModal<T extends { id: string }>({
  open = true,
  onSubmit,
  onClose,
  selected,
  title,
  searchQuery,
  getItemName,
  getItemSubtitle,
  getItemDetails,
  entityName,
  maxSelectedItems,
  maxSelectedItemsMessage,
  disabledIds,
}: SelectionModalProps<T>) {
  const { t: tCommon } = useTranslation();
  const { t: tGlobal } = useTranslation('global');

  const { search, debouncedSearch, updateSearch, clearSearch, hasSearch } = useSearching();

  const pageSize = 20;
  const [page, setPage] = useState(0);
  const pageOffset = page * pageSize;

  const { currentData, data, isFetching } = searchQuery({
    search: debouncedSearch ?? '',
    'pagination.Limit': pageSize,
    'pagination.Offset': pageOffset,
  });

  const searchedItems = useMemo(
    () => currentData?.items ?? (!open ? data?.items : []) ?? [],
    [currentData?.items, data?.items, open],
  );
  const totalCount = currentData?.totalCount || data?.totalCount || 0;

  const [selectedItems, setSelectedItems] = useState<T[]>(selected ?? []);

  const handleToggleItem = (item: T) => {
    const index = selectedItems.findIndex((x) => x.id === item.id);
    if (index >= 0) {
      // Prevent removing disabled items
      if (disabledIds?.includes(item.id)) return;

      const newSelectedItems = [...selectedItems];
      newSelectedItems.splice(index, 1);
      setSelectedItems(() => newSelectedItems);
    } else {
      const selectableItemsCount = selectedItems.filter((x) => !disabledIds?.includes(x.id)).length;
      if (maxSelectedItems && selectableItemsCount >= maxSelectedItems) {
        toast.error(
          maxSelectedItemsMessage ??
            tGlobal('building.maxSelectedItemsReached', {
              maxSelectedItems,
              entity: tGlobal(entityName, { context: 'plural' }),
            }),
        );
        return;
      }
      setSelectedItems(() => [...selectedItems, item]);
    }
  };

  const handleRemoveItem = (item: T) => {
    // Prevent removing disabled items
    if (disabledIds?.includes(item.id)) return;

    const index = selectedItems.findIndex((x) => x.id === item.id);
    if (index < 0) return;

    const newSelectedItems = [...selectedItems];
    newSelectedItems.splice(index, 1);
    setSelectedItems(() => newSelectedItems);
  };

  const isItemAssigned = (item: T) => selectedItems.findIndex((x) => x.id === item.id) >= 0;

  const handleChangeSearch = (value: string | null) => {
    setPage(0);
    updateSearch(value);
  };

  useEffect(() => {
    if (!open) return;
    setSelectedItems(selected ?? []);
  }, [open, selected]);

  return (
    <Modal open={open} appear size="3xl" onClose={onClose} onBackdropClick={onClose} title={title}>
      <ModalContent>
        <ItemsListAssignment
          selectedLabel={tCommon('selected')}
          isSubmitting={false}
          searchedItems={searchedItems}
          selectedItems={selectedItems.filter((item) => !disabledIds?.includes(item.id))}
          SearchedContainerHeader={
            <SearchField
              value={search}
              onChange={handleChangeSearch}
              onClear={clearSearch}
              placeholder={tCommon('search')}
              progressing={isFetching && hasSearch}
            />
          }
          SearchedContainerContent={(searchedItems) => (
            <Table size="xs" layout="fixed" className="relative w-full">
              <TableHead className="sticky top-0 z-50">
                <TableHeadRow>
                  <TableHeadIconCell className="w-[58px]" />
                  <TableHeadCell>{tGlobal(entityName)}</TableHeadCell>
                  {getItemDetails &&
                    getItemDetails(searchedItems[0])?.map((detail, idx) => (
                      <TableHeadCell key={idx}>{detail.label}</TableHeadCell>
                    ))}
                </TableHeadRow>
              </TableHead>

              {searchedItems.length <= 0 && !isFetching ? (
                <caption className="px-4 py-4 [caption-side:bottom]">
                  <EmptyPlaceholder>
                    {tCommon('noEntityFound', { entity: tGlobal(entityName, { context: 'plural' }) })}
                  </EmptyPlaceholder>
                </caption>
              ) : (
                <TableBody>
                  {isFetching &&
                    Array.from(Array(pageSize).keys()).map((x) => (
                      <TableRow key={x}>
                        <TableIconCell>
                          <Checkbox className="mr-2 ml-2" defaultChecked={false} disabled />
                        </TableIconCell>
                        <TableCell loading />
                      </TableRow>
                    ))}
                  {!isFetching &&
                    searchedItems
                      .filter((item: T) => !(disabledIds?.includes(item.id) && isItemAssigned(item)))
                      .map((searchedItem: T, idx: number) => (
                        <TableRow key={idx} onClick={() => handleToggleItem(searchedItem)} hover>
                          <TableIconCell>
                            <Checkbox className="mr-2 ml-2" checked={isItemAssigned(searchedItem)} readOnly />
                          </TableIconCell>
                          <TableCell className="text-ellipsis overflow-hidden">
                            <Text
                              className="text-sm font-medium whitespace-nowrap"
                              isEmpty={!isDefinedString(getItemName(searchedItem))}
                            >
                              {getItemName(searchedItem)}
                            </Text>
                            {getItemSubtitle && (
                              <Text
                                className="text-sm whitespace-nowrap"
                                isEmpty={!isDefinedString(getItemSubtitle(searchedItem))}
                              >
                                {getItemSubtitle(searchedItem)}
                              </Text>
                            )}
                          </TableCell>
                          {getItemDetails &&
                            getItemDetails(searchedItem)?.map((detail, idx) => (
                              <TableCell key={idx} className="text-ellipsis overflow-hidden">
                                <Text className="text-sm whitespace-nowrap" isEmpty={!isDefinedString(detail.value)}>
                                  {detail.value}
                                </Text>
                              </TableCell>
                            ))}
                        </TableRow>
                      ))}
                </TableBody>
              )}
            </Table>
          )}
          SearchedContainerFooter={
            <Pagination
              className="flex-1"
              totalCount={totalCount}
              currentPage={page}
              setPage={setPage}
              pageSize={pageSize}
              visible
            />
          }
          SelectedContainerContent={(selectedItems) => (
            <>
              {selectedItems
                .filter((item) => !disabledIds?.includes(item.id))
                .map((selectedItem, idx) => (
                  <div
                    key={idx}
                    className="flex items-center w-full text-left text-white rounded-lg bg-surface-selected px-5 py-3 shadow-md"
                  >
                    <div className="flex-1 overflow-hidden text-ellipsis">
                      <Text
                        as="div"
                        className="text-sm font-medium self-start whitespace-nowrap"
                        isEmpty={!isDefinedString(getItemName(selectedItem))}
                      >
                        {getItemName(selectedItem)}
                      </Text>
                      {getItemSubtitle && (
                        <Text
                          as="div"
                          className="text-sm self-start whitespace-nowrap"
                          isEmpty={!isDefinedString(getItemSubtitle(selectedItem))}
                        >
                          {getItemSubtitle(selectedItem)}
                        </Text>
                      )}
                    </div>

                    <Button size="xs" color="primary-invert" onClick={() => handleRemoveItem(selectedItem)}>
                      <FaRegTrashAlt />
                    </Button>
                  </div>
                ))}
            </>
          )}
        />
      </ModalContent>

      <ModalFooter>
        <Button
          disabled={selectedItems.length === 0 || isFetching || isEqualByIds(selectedItems, selected)}
          onClick={() => onSubmit(selectedItems)}
        >
          {tCommon('save')}
        </Button>
        <Button color="primary-dark" onClick={onClose}>
          {tCommon('close')}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
